import clsx from 'clsx'
import React, { forwardRef } from 'react'
import type { ComponentPropsWithoutRef, ForwardedRef, ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { Link } from '../shared/link'

const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:pointer-events-none disabled:opacity-50 [&_[data-slot=icon]]:pointer-events-none [&_[data-slot=icon]]:size-4 [&_[data-slot=icon]]:shrink-0 dark:focus-visible:ring-offset-zinc-950',
  {
    variants: {
      variant: {
        default: 'bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200',
        destructive: 'bg-rose-600 text-white hover:bg-rose-500 dark:bg-rose-600 dark:hover:bg-rose-500',
        outline: 'border border-zinc-200 bg-white hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950 dark:hover:bg-zinc-900',
        secondary: 'bg-zinc-100 text-zinc-900 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700',
        ghost: 'hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
        chip: 'border-transparent bg-transparent text-zinc-900 hover:bg-transparent dark:text-zinc-100 dark:hover:bg-transparent',
        link: 'text-zinc-900 underline-offset-4 hover:underline dark:text-zinc-100',
        unstyled: 'relative inline-flex focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-400 disabled:cursor-not-allowed disabled:opacity-60',
      },
      size: {
        default: 'h-10 px-4 py-2',
        chip: 'h-auto px-3 py-1 text-base/6',
        chipWide: 'h-auto px-4 py-1 text-base/6',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

type ButtonBaseProps = {
  className?: string
  children?: ReactNode
  href?: string
}

type ButtonActionProps = Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children' | 'href'> &
  Omit<ComponentPropsWithoutRef<'a'>, 'className' | 'children' | 'href'>

type ButtonProps = ButtonBaseProps &
  ButtonActionProps &
  VariantProps<typeof buttonVariants> & {
    asChild?: false
  }

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button({ className, children, href, disabled, type, onClick, size, variant = 'default', ...props }, ref) {
  const content = variant === 'unstyled' ? children : <TouchTarget>{children}</TouchTarget>
  const canonicalVariantClasses =
    variant === 'default' || variant === 'destructive'
      ? 'ui-button ui-button-primary dash-type-button rounded-full font-medium tracking-normal'
      : variant === 'secondary' || variant === 'outline' || variant === 'ghost'
        ? 'ui-button ui-button-secondary dash-type-button rounded-full font-medium tracking-normal'
        : variant === 'chip'
          ? 'ui-chip ui-chip-interactive dashboard-chip dashboard-chip-button dash-type-chip rounded-full font-medium tracking-normal'
        : null
  const classes = cn(
    buttonVariants({
      variant,
      size: variant === 'chip' ? (size ?? 'chip') : size,
      className:
        variant === 'unstyled'
          ? undefined
          : variant === 'chip'
            ? undefined
          : 'rounded-lg border text-base/6 font-semibold sm:text-sm/6 *:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:size-4',
    }),
    canonicalVariantClasses,
    className
  )

  return typeof href === 'string' ? (
    <Link
      {...(props as Omit<ComponentPropsWithoutRef<typeof Link>, 'className' | 'children' | 'href'>)}
      href={href}
      className={classes}
      aria-disabled={disabled ? 'true' : undefined}
      data-disabled={disabled ? 'true' : undefined}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault()
          return
        }
        onClick?.(event as never)
      }}
      ref={ref as ForwardedRef<HTMLAnchorElement>}
    >
      {content}
    </Link>
  ) : (
    <button
      {...(props as Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children'>)}
      type={type ?? 'button'}
      onClick={onClick as ComponentPropsWithoutRef<'button'>['onClick']}
      disabled={disabled}
      data-disabled={disabled ? 'true' : undefined}
      className={clsx(classes, variant !== 'unstyled' && 'cursor-pointer')}
      ref={ref as ForwardedRef<HTMLButtonElement>}
    >
      {content}
    </button>
  )
})

interface TouchTargetProps {
  children: ReactNode
}

export function TouchTarget({ children }: TouchTargetProps) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}
